import React, { useState } from "react"
import copy from "copy-to-clipboard"
import { Grid, IconButton, InputAdornment, TextField, Typography, Link } from "@material-ui/core"

import {
	ActionDialog,
	Loading,
	Breadcrumb,
	Notification,
	JsonEditor,
	InputDialog,
	AlertContainer
} from "@/components"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useHotjar from "@/hooks/useHotjar"

import { Close as CloseIcon, Search as SearchIcon } from "@material-ui/icons"

import ChatBotListSkeleton from "@/skeletons/Admin/ChatBotListSkeleton"
import ChatBotCreation from "@/pages/Admin/Flow/ChatBotCreation"
import FlowListItem from "@/pages/Admin/Flow/FlowList/FlowListItem"

import ErrorHandlerService from "@/services/ErrorHandler"
import ChatBotFlowService, { CategorizedChatBotFlows, ChatBotSearchQuery } from "@/services/ChatBotFlow"

import { ChatBotFlowTriggerType } from "@/protocols/chatBot"

import { whatsappLinks } from "@/utils/link"
import { isMoreOrEqualThanDaysAgo } from "@/utils/time"

import useStyles from "@/pages/Admin/ChatBot/ChatBotList/styles"
import { useGlobalStateStore } from "@/store/GlobalState"
import useDebounce from "@/hooks/useDebounce"

import { AnnouncementPopup } from "letalkui"
import Storage from "@/services/Storage"
import PreAttendanceBotOfferImage from "@/assets/images/popups/chatbot.png"

export const chatBotTriggerTranslatedByType: Record<ChatBotFlowTriggerType | "no-triggers", string> = {
	"attendance-started-by-client": "O atendimento é iniciado pelo cliente",
	"webhook-triggered": "Um Webhook é recebido",
	"no-triggers": "Os gatilhos ainda não foram configurados",
	"client-tag-associated": "Uma tag é associada ao contato",
	"attendance-finished": "O atendimento é finalizado",
	"attendance-taken": "O atendimento é assumido",
	"client-added-to-funnel-stage": "O contato é adicionado em uma etapa do funil"
}

const FlowList = () => {
	const [chatBotFlowList, setChatBotFlowList] = useState<CategorizedChatBotFlows>()
	const [loading, setLoading] = useState<boolean>(true)
	const [handleExportChatBotDialogOpen, setHandleExportChatBotDialogOpen] = useState(false)
	const [chatBotFlowJson, setChatBotFlowJson] = useState<string>()
	const [searchQuery, setSearchQuery] = useState<ChatBotSearchQuery>()

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()

	const minInstanceAgeInDaysForChatBotServiceOffer = 90
	const instanceIsOlderThanThreeMonths = isMoreOrEqualThanDaysAgo(globalStateStore.instance.instance_created_at, minInstanceAgeInDaysForChatBotServiceOffer)

	const preAttendanceBotOfferDismissedPermanentlyKey = "inbox:popup:permanent-dismissed:pre-attendance-bot-offer"
	const [isPreAttendanceBotOfferOpen, setIsPreAttendanceBotOfferOpen] = useState(
		!Storage.get<string>(preAttendanceBotOfferDismissedPermanentlyKey)
	)

	const hasPreAttendanceBot = Boolean(
		chatBotFlowList?.["attendance-started-by-client"]?.chat_bot_flow_in_chat_bot_triggers?.length
	)

	const shouldShowPreAttendanceBotOfferPopup = !loading && instanceIsOlderThanThreeMonths && !hasPreAttendanceBot && isPreAttendanceBotOfferOpen

	const handleClosePreAttendanceBotOfferPopup = () => {
		setIsPreAttendanceBotOfferOpen(false)
	}

	const handleDismissPreAttendanceBotOfferPopupPermanently = () => {
		Storage.set(preAttendanceBotOfferDismissedPermanentlyKey, "true")
		setIsPreAttendanceBotOfferOpen(false)
	}

	const handlePreAttendanceBotOfferDoNotShowAgainChange = (checked: boolean) => {
		if (checked) {
			Storage.set(preAttendanceBotOfferDismissedPermanentlyKey, "true")
		} else {
			Storage.delete(preAttendanceBotOfferDismissedPermanentlyKey)
		}
	}

	const { validation } = useValidation()

	const hotjar = useHotjar()
	hotjar.setupHotjar()

	const handleSearchChange = (searchText: string) => {
		setSearchQuery({ chatBotFlowName: searchText })
	}

	const handleGetChatBotList = async () => {
		setLoading(true)

		try {
			const categorizedChatBotFlows = await ChatBotFlowService.getAllCategorized("flow", searchQuery?.chatBotFlowName)

			setChatBotFlowList(categorizedChatBotFlows)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleExportChatBotOpen = async (chatBotFlowId: number) => {
		const exportedChatBotFlow = await ChatBotFlowService.export(chatBotFlowId)

		const serializedData = JSON.stringify(exportedChatBotFlow, null, 2)
		setChatBotFlowJson(serializedData)
		setHandleExportChatBotDialogOpen(true)
	}

	const handleExportChatBotClose = () => {
		setHandleExportChatBotDialogOpen(false)
	}

	const handleOnSaveExportChatBot = async () => {
		if (chatBotFlowJson) {
			copy(chatBotFlowJson)
			Notification.success({ message: "Dados do fluxo copiado com sucesso!" })
		}
		setHandleExportChatBotDialogOpen(false)
	}

	const handleUpdateBotName = (chatBotFlowId: number, newName: string, updatedAt: string): void => {
		setChatBotFlowList(previousCategorizedChatBotFlows => {
			if (!previousCategorizedChatBotFlows) {
				return previousCategorizedChatBotFlows
			}

			const updatedCategorizedChatBotFlows: CategorizedChatBotFlows = {} as CategorizedChatBotFlows

			Object.entries(previousCategorizedChatBotFlows).forEach(([botCategory, triggerWithBots]) => {
				const updatedBotsInCategory = triggerWithBots.chat_bot_flow_in_chat_bot_triggers.map(bot => {
					if (bot.id !== chatBotFlowId) {
						return bot
					}

					return {
						...bot,
						name: newName,
						updated_at: new Date(updatedAt)
					}
				})

				const botCategoryAsKey = botCategory as keyof CategorizedChatBotFlows

				updatedCategorizedChatBotFlows[botCategoryAsKey] = {
					...triggerWithBots,
					chat_bot_flow_in_chat_bot_triggers: updatedBotsInCategory
				}
			})

			return updatedCategorizedChatBotFlows
		})
	}

	const handleEditBotName = (chatBotFlowId: number, currentName: string) => {
		InputDialog.open({
			title: "Alterar nome do Bot",
			onOk: async (newChatBotName) => {
				try {
					await ChatBotFlowService.update(chatBotFlowId, { name: newChatBotName as string })
					Notification.success({ message: "Nome do bot alterado com sucesso!" })

					handleUpdateBotName(chatBotFlowId, newChatBotName as string, new Date().toISOString())

					return true
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
					Notification.error({ message: "Não foi possível alterar o nome do bot." })
					return false
				}
			},
			initialValue: currentName,
			fullWidth: true,
			customInputElement: (
				<TextField
					placeholder="Nome do Bot"
					variant="outlined"
					color="primary"
					fullWidth
				/>
			)
		})
	}

	// const getIsWillShowAllBotVersions = async () => {
	// 	const userInfo = await User.getInfo()

	// 	return userInfo?.botVersions.willShow
	// }

	useDidMount(async () => {
		// const isWillShowAllBotVersions = await getIsWillShowAllBotVersions()

		// if (isWillShowAllBotVersions) {
		// 	history.push("/admin/bots")
		// }

		handleGetChatBotList()
	})

	useDebounce(
		async () => {
			handleGetChatBotList()
		},
		searchQuery?.chatBotFlowName,
		2250
	)

	return (
		<Grid
			container
			direction="column"
			spacing={5}
		>
			<Grid item>

				<Grid container
					direction="column"
					spacing={6}
				>

					<Grid item>
						<Breadcrumb
							data={[
								{ name: "Bots", pathname: "/admin/automation/flow" }
							]}
						/>
					</Grid>

					<Grid item>
						<Grid
							container
							spacing={2}
							justifyContent="space-between"
							alignItems="center"
							wrap="nowrap"
						>
							<Grid item>
								<Typography
									variant="h5"
									color="textPrimary"
									className={classes.title}
								>
										Bots
								</Typography>
							</Grid>

							<Grid
								item
							>
								<Grid container
									spacing={2}
									wrap="nowrap"
								>
									<Grid item>
										<TextField
											style={{ flexWrap: "nowrap" }}
											value={searchQuery?.chatBotFlowName}
											onChange={({ target }) =>
												handleSearchChange(target.value)
											}
											placeholder="Pesquisar"
											variant="outlined"
											InputProps={{
												startAdornment: (
													<InputAdornment style={{ minHeight: 52 }} position="start">
														<SearchIcon />
													</InputAdornment>
												),
												endAdornment: searchQuery?.chatBotFlowName && (
													<IconButton
														size="small"
														onClick={() => handleSearchChange("")}
													>
														<CloseIcon
															fontSize="small"
														/>
													</IconButton>
												)
											}}
											fullWidth
											size="small"
											helperText={validation.search}
											error={!!validation.search}
										/>
									</Grid>
									<Grid item>
										<ChatBotCreation
											handleGetChatBotList={handleGetChatBotList}
											onChatBotCreated={handleGetChatBotList}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12}>
				{instanceIsOlderThanThreeMonths && (
					<AlertContainer
						alertType="info"
						title="Quer um chatbot criado pela Letalk?"
					>
						<Typography variant="body2">
							Um especialista entende sua dor numa call de 30 min e constrói o chatbot ideal pra sua
							operação — fluxo, mensagens, testes e publicação, com até 25 blocos por bot. {" "}
							<Link
								href={whatsappLinks.sendMessageToRequestChatBotCreationService}
								underline="always"
								target="_blank"
								color="inherit"
							>
								Falar com a Letalk
							</Link>
						</Typography>
					</AlertContainer>
				)}
			</Grid>

			<Grid item xs={12}>
				<Loading
					loading={loading}
					customLoadingElement={<ChatBotListSkeleton />}
				>

					<Grid
						container
						spacing={5}
					>
						{Object.values(chatBotFlowList || {} as CategorizedChatBotFlows).map(triggerWithChatBotFlows =>
							triggerWithChatBotFlows.chat_bot_flow_in_chat_bot_triggers?.length > 0 && (
								<Grid
									item
									xs={12}
									key={triggerWithChatBotFlows.type}
								>
									<Typography
										variant="h4"
										color="textPrimary"
										className={classes.eventTrigger}
										gutterBottom
									>
										{triggerWithChatBotFlows.type !== "no-triggers"
											? "BOTS ACIONADOS QUANDO:"
											: "BOTS QUE:"}
									</Typography>

									<Typography
										variant="h4"
										className={classes.descriptionTrigger}
										gutterBottom
									>
										{chatBotTriggerTranslatedByType[triggerWithChatBotFlows.type]}
									</Typography>

									<Grid
										container
										spacing={3}
									>
										{triggerWithChatBotFlows.chat_bot_flow_in_chat_bot_triggers.map(bot => (
											<Grid
												item
												xs={12}
												key={bot.id}
											>
												<FlowListItem
													chatBotFlow={{
														active: bot.active,
														updated_at: bot.updated_at,
														name: bot.name,
														id: bot.id,
														consolidated_data: bot.consolidated_data,
														tag: bot?.tag,
														board: bot?.board
													}}
													chatBotCategory={triggerWithChatBotFlows.type}
													flowEditorPath={`/admin/automation/flow/${bot.id}`}
													webhookTemplateType={bot?.webhook_template?.type}
													onExport={() => handleExportChatBotOpen(bot.id)}
													onEdit={() => handleEditBotName(bot.id, bot.name)}
												/>
											</Grid>
										))}
									</Grid>
								</Grid>
							)
						)}
					</Grid>
				</Loading>
			</Grid>

			<ActionDialog
				title="Exportar Bot"
				saveText="Copiar código"
				openDialog={handleExportChatBotDialogOpen}
				onSave={handleOnSaveExportChatBot}
				onClose={handleExportChatBotClose}
				fullWidth
			>
				<JsonEditor
					className={classes.disableSelection}
					height="300px"
					defaultValue={chatBotFlowJson}
					readOnly
				/>
			</ActionDialog>

			{shouldShowPreAttendanceBotOfferPopup && (
				<AnnouncementPopup
					announcement={{
						type: "pre-attendance-bot-offer",
						image: PreAttendanceBotOfferImage,
						title: "Transforme o primeiro contato em oportunidade",
						body: "Enquanto seu time não entra na conversa, o bot de pré-atendimento já responde, direciona e mantém o contato engajado — sem deixar nenhum lead esperando. Te ajudamos a estruturar esse fluxo sem perder a naturalidade do atendimento.",
						confirmButtonText: "Falar com a Letalk",
						onAction: () => window.open(whatsappLinks.sendMessageToRequestChatBotCreationService, "_blank")
					}}
					open={shouldShowPreAttendanceBotOfferPopup}
					onClose={handleClosePreAttendanceBotOfferPopup}
					onClosePermanently={handleDismissPreAttendanceBotOfferPopupPermanently}
					onDoNotShowAgainChange={handlePreAttendanceBotOfferDoNotShowAgainChange}
				/>
			)}

		</Grid>
	)
}

export default FlowList
